import {
	EnvelopeIcon,
	ExclamationTriangleIcon,
	PhoneIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';

const formatPhone = (phone) => {
	return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6, 10)}`;
};

const ServiceCard = ({
	zipCode,
	program,
	taxonomyId,
	taxonomyDetail,
	onOrgChosen,
}) => {
	const isTandemOrgService = !!program?.ccAttributes?.coord_agency_id;
	const acceptsElectronicReferralsTag = program?.tags.find(
		({ category }) => category === 'accepts_e_ref',
	);
	const acceptsElectronicReferrals = !!acceptsElectronicReferralsTag;
	const acceptsElectronicReferralsCurrently =
		acceptsElectronicReferralsTag?.properties.find(
			({ name }) => name === 'accepts_e_ref_currently',
		)?.value === '1';

	return (
		<div className="card">
			<div className="card-body">
				<div className="flex flex-row gap-8">
					<div className="flex-1">
						{zipCode ? (
							<>
								{program.locations.map((location) => (
									<div key={location.id}>
										<h2>
											{location.organization_name}
											{program?.ccAttributes?.coord_agency_id && (
												<CheckCircleIcon className="inline-block w-4 h-4 mb-1 ml-1 text-success" />
											)}
										</h2>
									</div>
								))}
							</>
						) : (
							<h2>{program.name}</h2>
						)}
						{program.physical_address.map((address) => (
							<div key={`${address.id}`}>
								{address.address_1} | {address.city}, {address.state_province},{' '}
								{address.postal_code}
							</div>
						))}
						<div className="description">{program.description}</div>
						<h4>Eligibility</h4>
						<div className="text-sm">{program.eligibility}</div>
						{!isEmpty(program.required_document) && (
							<>
								<h4>Documents Required</h4>
								<div className="text-sm">{program.required_document}</div>
							</>
						)}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<h4>Contact</h4>
								<div className="text-sm">
									{program.phones.map(({ number }) => (
										<div key={`${number}`}>
											<PhoneIcon className="inline-block w-4 h-4 mb-1 mr-1" />
											{formatPhone(number)}
										</div>
									))}
									{!isEmpty(program.email) && (
										<div>
											<EnvelopeIcon className="inline-block w-4 h-4 mr-1" />
											<a className="link" href={`mailto:${program.email}`}>
												{program.email}
											</a>
										</div>
									)}
								</div>
							</div>
							{!isEmpty(program.service_area) && (
								<div>
									<h4>Serves</h4>
									<div className="text-sm">{program.service_area}</div>
								</div>
							)}
							{!isEmpty(program.schedule_notes) && (
								<div>
									<h4>Service Hours</h4>
									<div className="text-sm">{program.schedule_notes}</div>
								</div>
							)}
							{!isEmpty(program.application_process) && (
								<div>
									<h4>Application Process</h4>
									<div className="text-sm">{program.application_process}</div>
								</div>
							)}
						</div>
					</div>
					<div className="grid-cols-1 w-36 card-actions">
						{program?.ccAttributes?.coord_agency_id && (
							<>
								{program.ccAttributes?.availability != 1 && (
									<div role="alert" className="gap-2 p-2 alert alert-warning">
										<ExclamationTriangleIcon className="inline-block w-6 h-6" />
										<span>Temporarily unavailable</span>
									</div>
								)}
								{program.ccAttributes?.availability == 1 &&
									program.ccAttributes?.waitlist_ind == 1 && (
										<div role="alert" className="gap-2 p-2 alert alert-warning">
											<ExclamationTriangleIcon className="inline-block w-6 h-6" />
											<span>Wait list only</span>
										</div>
									)}
							</>
						)}
					</div>
				</div>
				<div className="mt-4 text-right">
					{acceptsElectronicReferrals || isTandemOrgService ? (
						<button
							className="btn-wide btn btn-primary btn-lg"
							data-taxonomy-id={taxonomyId}
							data-taxonomy-detail={taxonomyDetail}
							onClick={(event) => onOrgChosen(event, program)}
							disabled={!acceptsElectronicReferralsCurrently}
						>
							Apply Electronically
							<ChevronRightIcon className="w-4 h-4" />
						</button>
					) : (
						<button
							className="btn-wide btn btn-primary btn-lg btn-outline"
							data-taxonomy-id={taxonomyId}
							data-taxonomy-detail={taxonomyDetail}
							onClick={(event) => onOrgChosen(event, program)}
						>
							Verbal Referral
							<ChevronRightIcon className="w-4 h-4" />
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

ServiceCard.propTypes = {
	zipCode: PropTypes.string,
	program: PropTypes.object.isRequired,
	taxonomyId: PropTypes.string,
	taxonomyDetail: PropTypes.string,
	onOrgChosen: PropTypes.func.isRequired,
};

export default ServiceCard;
